import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import classNames from "classnames";
import styles from "./ItemPresetModal.module.scss";
import Button from "../../button/Button";
import { toast } from "react-toastify";
import { CloseIcon, DeleteIcon } from "../../../assets";
import vendingControllersService from "../../../apis/services/admin/vendingControllersService";
import {
  MACHINE_TYPES,
  TAX_LABEL_PDV,
  TAX_LABEL_NO_PDV,
  generatePresetItems,
} from "../../../util/itemPresets";

const ItemPresetModal = ({
  modalOpen,
  onClose,
  vendingControllerId,
  existingItems,
  onSuccess,
}) => {
  const [machineType, setMachineType] = useState("zetonjera");
  const [protocol, setProtocol] = useState("cctalk");
  const [tokenValue, setTokenValue] = useState("100");
  const [dinPerPulse, setDinPerPulse] = useState("100");
  const [isPdv, setIsPdv] = useState(true);
  const [rows, setRows] = useState([]);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [saving, setSaving] = useState(false);

  const existingCount = (existingItems || []).length;

  useEffect(() => {
    if (!modalOpen) return;
    setMachineType("zetonjera");
    setProtocol("cctalk");
    setTokenValue("100");
    setDinPerPulse("100");
    setIsPdv(true);
    setReplaceExisting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // Svaka promena parametara regeneriše redove (ručne izmene se tad gube —
  // parametre biraj prvo, redove doteruj na kraju)
  useEffect(() => {
    if (!modalOpen) return;
    const parsedTokenValue = parseFloat(tokenValue);
    const parsedDinPerPulse = parseFloat(dinPerPulse);
    if (
      (machineType === "zetonjera" &&
        (!parsedTokenValue || parsedTokenValue <= 0)) ||
      (protocol === "impuls" && (!parsedDinPerPulse || parsedDinPerPulse <= 0))
    ) {
      setRows([]);
      return;
    }
    setRows(
      generatePresetItems({
        machineType,
        protocol,
        dinPerPulse: parsedDinPerPulse,
        tokenValue: parsedTokenValue,
      }).map((row) => ({ ...row, unitPrice: String(row.unitPrice) }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, machineType, protocol, tokenValue, dinPerPulse]);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((row, i) => i !== index));
  };

  const rowsValid = rows.every(
    (row) =>
      row.name.trim() !== "" &&
      row.identifier.trim() !== "" &&
      parseFloat(row.unitPrice) > 0
  );

  const onSubmit = async () => {
    if (saving || rows.length === 0 || !rowsValid) {
      return;
    }
    setSaving(true);
    const label = isPdv ? TAX_LABEL_PDV : TAX_LABEL_NO_PDV;
    const errors = [];
    if (replaceExisting) {
      for (const existing of existingItems || []) {
        try {
          await vendingControllersService.deleteItem({
            id: vendingControllerId,
            itemId: existing.id,
          });
        } catch (err) {
          errors.push(
            `brisanje "${existing.name}": ${
              err?.response?.data?.message || err?.message || "greška"
            }`
          );
        }
      }
    }
    let added = 0;
    for (const row of rows) {
      try {
        await vendingControllersService.addItemToVendingController({
          id: vendingControllerId,
          item: {
            name: row.name.trim(),
            identifier: row.identifier.trim(),
            unitPrice: parseFloat(row.unitPrice),
            labels: [label],
          },
        });
        added += 1;
      } catch (err) {
        errors.push(
          `${row.name}: ${
            err?.response?.data?.message || err?.message || "greška"
          }`
        );
      }
    }
    setSaving(false);
    if (errors.length === 0) {
      toast.info(`Dodato ${added} stavki (labela ${label})`);
    } else {
      toast.warn(`Dodato ${added}/${rows.length} — ${errors.join("; ")}`);
    }
    onSuccess();
  };

  return (
    <Modal open={modalOpen} onClose={onClose}>
      <div className={styles.modalWrap}>
        <div className={styles.modalInner}>
          <div className={styles.header}>
            <div className={styles.closeIconWrap}>
              <img
                src={CloseIcon}
                alt="close"
                onClick={onClose}
                className={styles.closeIcon}
              />
            </div>
            <p className={styles.title}>Primeni šablon stavki</p>
          </div>
          <div className={styles.content}>
            <div className={styles.paramsGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tip mašine</label>
                <select
                  className={styles.select}
                  value={machineType}
                  onChange={(event) => setMachineType(event.target.value)}
                >
                  {MACHINE_TYPES.map((type) => (
                    <option key={type.key} value={type.key}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Protokol</label>
                <select
                  className={styles.select}
                  value={protocol}
                  onChange={(event) => setProtocol(event.target.value)}
                >
                  <option value="cctalk">CCTalk (c001–c008)</option>
                  <option value="impuls">Impuls (B + broj impulsa)</option>
                </select>
              </div>
              {machineType === "zetonjera" && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Vrednost žetona (din)</label>
                  <input
                    type="number"
                    min="1"
                    className={styles.input}
                    value={tokenValue}
                    onChange={(event) => setTokenValue(event.target.value)}
                  />
                </div>
              )}
              {protocol === "impuls" && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Dinara po impulsu</label>
                  <input
                    type="number"
                    min="1"
                    className={styles.input}
                    value={dinPerPulse}
                    onChange={(event) => setDinPerPulse(event.target.value)}
                  />
                </div>
              )}
            </div>
            <label className={styles.label}>Poreska labela (za sve stavke)</label>
            <div className={styles.labelChoices}>
              <button
                type="button"
                className={classNames(
                  styles.labelButton,
                  isPdv && styles.labelButtonActive
                )}
                onClick={() => setIsPdv(true)}
              >
                {TAX_LABEL_PDV} — u PDV-u
              </button>
              <button
                type="button"
                className={classNames(
                  styles.labelButton,
                  !isPdv && styles.labelButtonActive
                )}
                onClick={() => setIsPdv(false)}
              >
                {TAX_LABEL_NO_PDV} — nije u PDV-u
              </button>
            </div>
            <div className={styles.rowsHeader}>
              <p>NAZIV</p>
              <p>IDENTIFIKATOR</p>
              <p>CENA</p>
              <p></p>
            </div>
            <div className={styles.rowsArea}>
              {rows.map((row, index) => (
                <div className={styles.entryRow} key={`${row.identifier}-${index}`}>
                  <input
                    className={styles.entryInput}
                    value={row.name}
                    onChange={(event) => updateRow(index, "name", event.target.value)}
                  />
                  <input
                    className={styles.entryInput}
                    value={row.identifier}
                    onChange={(event) =>
                      updateRow(index, "identifier", event.target.value)
                    }
                  />
                  <input
                    type="number"
                    className={styles.entryInput}
                    value={row.unitPrice}
                    onChange={(event) =>
                      updateRow(index, "unitPrice", event.target.value)
                    }
                  />
                  <img
                    src={DeleteIcon}
                    alt="obriši"
                    className={styles.rowDelete}
                    onClick={() => removeRow(index)}
                  />
                </div>
              ))}
              {rows.length === 0 && (
                <p className={styles.emptyHint}>
                  Nema stavki za prikaz — proveri parametre (vrednost žetona /
                  dinara po impulsu).
                </p>
              )}
            </div>
            {existingCount > 0 && (
              <label className={styles.replaceRow}>
                <input
                  type="checkbox"
                  checked={replaceExisting}
                  onChange={(event) => setReplaceExisting(event.target.checked)}
                />
                Obriši postojeće stavke ({existingCount}) pre primene
              </label>
            )}
            <div className={styles.actions}>
              <Button
                name={
                  saving
                    ? "Primenjujem..."
                    : `Primeni (${rows.length} stavki)`
                }
                onClick={onSubmit}
                disabled={saving || rows.length === 0 || !rowsValid}
                showLoader={saving}
              />
              <Button name="Otkaži" onClick={onClose} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ItemPresetModal;
